import Button from '@jetbrains/ring-ui/components/button/button'
import Dialog from '@jetbrains/ring-ui/components/dialog/dialog'
import {H3} from '@jetbrains/ring-ui/components/heading/heading'
import {Content, Header} from '@jetbrains/ring-ui/components/island/island'
import Panel from '@jetbrains/ring-ui/components/panel/panel'
import Radio from '@jetbrains/ring-ui/components/radio/radio'
import classNames from 'classnames'
import * as React from 'react'

import {getBuildType} from '../../../../selectors'
import {ROOT_PROJECT_ID} from '../../../../types'
import {noop} from '../../../../utils/empty'
import {resolveHelpURL} from '../../../../utils/url'
import HelpLink from '../../../common/HelpLink/HelpLink'
import IconButton from '../../../common/IconButton/IconButton'
import {getCustomError} from '../CleanupProjectPage.selectors'
import type {UpdatePolicyPayload, HolderType, PolicyEntry} from '../CleanupProjectPage.types'
import {getPolicyHolderIds} from '../CleanupProjectPage.utils'
import Form from '../Form/Form'
import ArtifactsPatternInput from '../Listings/Rules/CleanupForm/Sections/ArtifactsSection'

import {actions} from './EditForm.consts'
import {getInitialState, reducer} from './EditForm.reducers'
import type {OwnProps, EditFormState, Entity, FormGroup} from './EditForm.types'
import {adaptFormToPolicy, VALIDATIONS} from './EditForm.utils'

import styles from './EditForm.css'

import {useAppSelector} from 'src/hooks/react-redux'

// eslint-disable-next-line @typescript-eslint/no-magic-numbers
const JAVA_MAX_INT = 2 ** 31 - 1

function NumericInput(props: React.JSX.IntrinsicElements['input']) {
  return <input type="number" min={1} max={JAVA_MAX_INT} className={styles.input} {...props} />
}

type ActionsSectionProps = {
  actionPerforming?: boolean
  onCancelClick?: (event: React.MouseEvent<HTMLButtonElement>) => void
}
function ActionsSection({actionPerforming, onCancelClick}: ActionsSectionProps) {
  const error = useAppSelector(getCustomError)
  return (
    <Panel className={styles.panel}>
      <Button primary type="submit" loader={actionPerforming}>
        {'Update'}
      </Button>
      <Button onClick={onCancelClick} loader={actionPerforming}>
        {'Cancel'}
      </Button>
      {error != null && (
        <div className={styles.error}>
          <div key={error.errorId}>{error.message}</div>
        </div>
      )}
    </Panel>
  )
}

type EverythingSectionProps = {
  data: FormGroup
  inputChangeHandler(event: React.ChangeEvent<HTMLInputElement>): void
  setRadioVisible(value: string): void
}
const EverythingSection = React.memo(
  ({data, inputChangeHandler, setRadioVisible}: EverythingSectionProps) => (
    <fieldset className={styles.section}>
      <legend className={styles.legend}>
        <H3 className={styles.title}>{'Clean everything'}</H3>
        <p className={styles.description}>
          {'Clean everything including artifacts, history, and statistical data'}
        </p>
      </legend>
      <div className={styles.radioWrapper}>
        <div className={styles.radioGroup}>
          <Radio className={styles.radio} onChange={setRadioVisible}>
            <Radio.Item
              key={'everything.radio.default'}
              value={'everything.radio.default'}
              checked={!data.custom}
            >
              {'Default policy'}
            </Radio.Item>
            <Radio.Item
              key={'everything.radio.custom'}
              value={'everything.radio.custom'}
              checked={data.custom}
            >
              {'Custom policy'}
            </Radio.Item>
          </Radio>
        </div>
        {!data.custom && <p className={styles.defaultPolicy}>{data.default ?? 'Never'}</p>}
      </div>
      {data.custom && (
        <div className={styles.inputs}>
          <div className={styles.inputGroup}>
            <label className={styles.label} htmlFor="everything.input.days">
              {'Older than '}
            </label>
            <label className={styles.postLabel}>
              <NumericInput
                value={data.days ?? ''}
                id="everything.input.days"
                onChange={inputChangeHandler}
              />
              {'days since last build'}
            </label>
          </div>
          <div className={styles.inputGroup}>
            <span className={styles.label} />
            <label className={styles.postLabel}>
              <NumericInput
                value={data.builds ?? ''}
                id="everything.input.builds"
                onChange={inputChangeHandler}
              />
              {'-th successful build'}
            </label>
          </div>
          {data.error != null && (
            <div className={styles.inputGroup}>
              <span className={styles.label} /> <span className={styles.error}>{data.error}</span>
            </div>
          )}
        </div>
      )}
    </fieldset>
  ),
)
EverythingSection.displayName = 'EverythingSection'

type ArtifactsSectionProps = {
  data: FormGroup
  onArtifactsPatternChange(value: string): void
  inputChangeHandler(event: React.ChangeEvent<HTMLInputElement>): void
  setRadioVisible(value: string): void
  policyEntry: PolicyEntry
  type: HolderType
}
const ArtifactsSection = React.memo(
  ({
    data,
    onArtifactsPatternChange,
    inputChangeHandler,
    setRadioVisible,
    policyEntry,
    type,
  }: ArtifactsSectionProps) => {
    const {buildTypeId} = getPolicyHolderIds(policyEntry, type)
    const isComposite = useAppSelector(
      state => getBuildType(state, buildTypeId)?.type === 'composite',
    )

    if (isComposite) {
      return null
    }

    return (
      <fieldset className={styles.section}>
        <legend className={styles.legend}>
          <H3 className={styles.title}>{'Clean artifacts'}</H3>
          <p className={styles.description}>{'keep history and statistical data'}</p>
        </legend>
        <div className={styles.radioWrapper}>
          <div className={styles.radioGroup}>
            <Radio className={styles.radio} onChange={setRadioVisible}>
              <Radio.Item
                key={'artifacts.radio.default'}
                value={'artifacts.radio.default'}
                checked={!data.custom}
              >
                {'Default policy'}
              </Radio.Item>
              <Radio.Item
                key={'artifacts.radio.custom'}
                value={'artifacts.radio.custom'}
                checked={data.custom}
              >
                {'Custom policy'}
              </Radio.Item>
            </Radio>
          </div>
          {!data.custom && <p className={styles.defaultPolicy}>{data.default ?? 'Never'}</p>}
        </div>
        {data.custom && (
          <div className={styles.inputs}>
            <div className={styles.inputGroup}>
              <label className={styles.label} htmlFor="artifacts.input.days">
                {'Older than'}
              </label>
              <label className={styles.postLabel}>
                <NumericInput
                  value={data.days ?? ''}
                  id="artifacts.input.days"
                  onChange={inputChangeHandler}
                />
                {'days since last build'}
              </label>
            </div>
            <div className={styles.inputGroup}>
              <span className={styles.label} />
              <label className={styles.postLabel}>
                <NumericInput
                  value={data.builds ?? ''}
                  id="artifacts.input.builds"
                  onChange={inputChangeHandler}
                />
                {'-th successful build'}
              </label>
            </div>
            {data.error != null && (
              <div className={styles.inputGroup}>
                <span className={styles.label} />
                <span className={styles.error}>{data.error}</span>
              </div>
            )}
            <div className={styles.inputGroup}>
              <label className={styles.label} htmlFor="artifacts.input.days">
                {'Artifact patterns '}
              </label>
              <ArtifactsPatternInput
                value={data.pattern}
                onChange={onArtifactsPatternChange}
                className={styles.textareaWrapper}
                id="artifacts.input.days"
              />
            </div>
          </div>
        )}
      </fieldset>
    )
  },
)
ArtifactsSection.displayName = 'ArtifactsSection'

type HistorySectionProps = {
  data: FormGroup
  inputChangeHandler(event: React.ChangeEvent<HTMLInputElement>): void
  setRadioVisible(value: string): void
}
const HistorySection = React.memo(
  ({data, inputChangeHandler, setRadioVisible}: HistorySectionProps) => (
    <fieldset className={styles.section}>
      <legend className={styles.legend}>
        <H3 className={styles.title}>{'Clean history'}</H3>
        <p className={styles.description}>{'keep statistical data'}</p>
      </legend>
      <div className={styles.radioWrapper}>
        <div className={styles.radioGroup}>
          <Radio className={styles.radio} onChange={setRadioVisible}>
            <Radio.Item
              key={'history.radio.default'}
              value={'history.radio.default'}
              checked={!data.custom}
            >
              {'Default policy'}
            </Radio.Item>
            <Radio.Item
              key={'history.radio.custom'}
              value={'history.radio.custom'}
              checked={data.custom}
            >
              {'Custom policy'}
            </Radio.Item>
          </Radio>
        </div>
        {!data.custom && <p className={styles.defaultPolicy}>{data.default ?? 'Never'}</p>}
      </div>
      {data.custom && (
        <div className={styles.inputs}>
          <div className={styles.inputGroup}>
            <label className={styles.label} htmlFor="history.input.days">
              {'Older than '}
            </label>
            <label className={styles.postLabel}>
              <NumericInput
                value={data.days ?? ''}
                id="history.input.days"
                onChange={inputChangeHandler}
              />
              {'days since last build'}
            </label>
          </div>
          <div className={styles.inputGroup}>
            <span className={styles.label} />
            <label className={styles.postLabel}>
              <NumericInput
                value={data.builds ?? ''}
                id="history.input.builds"
                onChange={inputChangeHandler}
              />
              {'-th successful build'}
            </label>
          </div>
          {data.error != null && (
            <div className={styles.inputGroup}>
              <span className={styles.label} /> <span className={styles.error}>{data.error}</span>
            </div>
          )}
        </div>
      )}
    </fieldset>
  ),
)
HistorySection.displayName = 'HistorySection'

type ArtifactsDependenciesSectionProps = {
  dependencies: string
  setDependencies(value: string): void
  isRoot: boolean
}
const ArtifactsDependenciesSection = React.memo(
  ({dependencies, setDependencies, isRoot}: ArtifactsDependenciesSectionProps) => (
    <fieldset className={styles.section}>
      <legend className={styles.legend}>
        <H3 className={styles.title}>{'Dependencies'}</H3>
        <p className={styles.description}>
          {
            'This\u00a0setting affects clean-up of\u00a0artifacts in\u00a0builds of\u00a0the\u00a0dependency build\u00a0configurations'
          }
        </p>
      </legend>
      <div className={styles.radioWrapper}>
        <div className={styles.radioGroup}>
          <Radio className={styles.radio} onChange={setDependencies}>
            {!isRoot && (
              <Radio.Item
                key={'dependencies.radio.default'}
                value={'default'}
                checked={dependencies === 'default'}
              >
                {'Use default'}
              </Radio.Item>
            )}
            <Radio.Item
              key={'dependencies.radio.prevent'}
              value={'prevent'}
              checked={dependencies === 'prevent'}
            >
              {'Prevent clean-up'}
            </Radio.Item>
            <Radio.Item
              key={'dependencies.radio.do_not_prevent'}
              value={'do_not_prevent'}
              checked={dependencies === 'do_not_prevent'}
            >
              {'Do not prevent clean-up'}
            </Radio.Item>
          </Radio>
        </div>
      </div>
    </fieldset>
  ),
)
ArtifactsDependenciesSection.displayName = 'ArtifactsDependenciesSection'

function EditForm({
  policyEntry,
  type,
  onCancelClick,
  onCompleteClick = noop,
  formVisible,
  actionPerforming,
}: OwnProps) {
  const [state, dispatch] = React.useReducer(reducer, getInitialState(policyEntry))
  const setRadioVisible = React.useCallback(
    (value: string) => {
      const [entity, _, val] = value.split('.')
      dispatch({
        value: val,
        entity: entity as Entity,
        type: actions.SET_CUSTOM,
      })
    },
    [dispatch],
  )
  const setDependencies = React.useCallback(
    (value: EditFormState['dependencies']) =>
      dispatch({
        type: actions.SET_DEPENDENCIES,
        value,
      }),
    [dispatch],
  )
  const inputChangeHandler = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!event.target.checkValidity()) {
        return
      }

      let value: number | null = parseInt(event.target.value.replace(/([^0-9])/g, ''), 10)

      if (isNaN(value)) {
        value = null
      }

      const id = event.target.id
      const [entity, _, kind] = id.split('.')
      dispatch({
        type: actions.SET_VALUE,
        entity: entity as Entity,
        kind: kind as keyof FormGroup,
        value,
      })
    },
    [dispatch],
  )
  const onArtifactsPatternChange = React.useCallback(
    (value: string) => {
      dispatch({
        type: actions.SET_ARTIFACTS_VALUE,
        value,
      })
    },
    [dispatch],
  )
  const validateForm = React.useCallback(() => {
    const {artifacts, everything, history} = state
    let valid = true

    const reportErrorIfExists = (entity: Entity, error: string | null) => {
      if (error == null) {
        return
      }

      valid = false
      dispatch({
        type: actions.SET_ERROR,
        entity,
        error,
      })
    }

    reportErrorIfExists('artifacts', VALIDATIONS.not_specified(artifacts))
    reportErrorIfExists('history', VALIDATIONS.not_specified(history))
    reportErrorIfExists('everything', VALIDATIONS.not_specified(everything))
    return valid
  }, [state])

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const valid = validateForm()

    if (!valid) {
      return
    }

    const {artifacts, everything, history, dependencies} = state
    const result: UpdatePolicyPayload = adaptFormToPolicy({
      artifacts,
      everything,
      history,
      dependencies,
      policyEntry,
      type,
    })
    onCompleteClick(result)
  }

  const policyName = policyEntry?.name ? policyEntry.name.split(' / ').pop() : ''
  const title = `Base\u00a0rule for\u00a0${policyName}\u2060`
  return (
    <Dialog
      label={title}
      contentClassName={styles.editForm}
      show={formVisible}
      onEscPress={onCancelClick}
      onCloseClick={onCancelClick}
      trapFocus
      autoFocusFirst={false}
      showCloseButton
    >
      <Header>{title}</Header>
      <div className={classNames(styles.description, styles.headerHint)}>
        {`
          Base\u00a0rule allows for\u00a0preserving build\u00a0data using simple settings. The\u00a0rule is\u00a0applied considering branches, personal and canceled\u00a0builds,\u00a0etc.
        `}
        <HelpLink
          url={resolveHelpURL('clean-up.html#Clean-up-BaseRule')}
          Component={IconButton}
          icon="help"
        />
      </div>
      <Form onSubmit={onSubmit} className={styles.form}>
        <Content>
          <div>
            <ArtifactsSection
              data={state.artifacts}
              onArtifactsPatternChange={onArtifactsPatternChange}
              inputChangeHandler={inputChangeHandler}
              setRadioVisible={setRadioVisible}
              policyEntry={policyEntry}
              type={type}
            />
            <HistorySection
              data={state.history}
              inputChangeHandler={inputChangeHandler}
              setRadioVisible={setRadioVisible}
            />
            <EverythingSection
              data={state.everything}
              inputChangeHandler={inputChangeHandler}
              setRadioVisible={setRadioVisible}
            />
            <ArtifactsDependenciesSection
              dependencies={state.dependencies}
              setDependencies={setDependencies}
              isRoot={policyEntry.externalId === ROOT_PROJECT_ID}
            />
          </div>
        </Content>
        <ActionsSection actionPerforming={actionPerforming} onCancelClick={onCancelClick} />
      </Form>
    </Dialog>
  )
}

export default EditForm
